import { useEffect } from "react";
import { useAppDispatch } from "@/redux/features/hooks";
import { useMercureSubscription } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { RegisterSessionApi } from "@/redux/services/pos/registerSession.api";
import type { RegisterSession } from "@/redux/services/pos/pos.type";

const SESSION_EVENTS = [
  "cash_register_session:opened",
  "cash_register_session:closed",
  "cash_register_session:updated",
] as const;

/**
 * Subscribes to the restaurant's cash-register-session Mercure topic and
 * invalidates the cached session queries whenever the active session
 * changes — opened/closed (e.g. by a manager from another device) or
 * updated (e.g. balance bumped after a cash payment). Any open
 * `useGetOpenSessionQuery` / `useGetSessionsQuery` will refetch
 * automatically.
 *
 * Mount this once at a high level (e.g. DashLayout) — it's cheap (one SSE
 * connection per restaurant) and keeps every consumer of the session in
 * sync without polling.
 *
 * Only subscribes when the user holds `cash_register.view`, the permission
 * the API requires for this topic in the Mercure subscriber JWT
 * (SFRESTO-181). Kitchen and waiter users therefore get no cash-register
 * events.
 */
export function useRegisterSessionRealtime(restaurantId: string | undefined) {
  const dispatch = useAppDispatch();
  const { can } = usePermissions();
  const canViewRegister = can("cash_register.view");
  const event = useMercureSubscription<RegisterSession>(
    restaurantId && canViewRegister
      ? `/api/restaurants/${restaurantId}/cash_register_sessions`
      : null,
    SESSION_EVENTS,
  );

  useEffect(() => {
    if (!event) return;
    dispatch(
      RegisterSessionApi.util.invalidateTags([
        "registerSession",
        "registerSessionPayment",
        "registerSessionLedger",
      ]),
    );
  }, [event, dispatch]);
}
